import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
} from '@/core/http-methods';
import { ListElementType } from '@/domain/basic';
import {
  FieldsType,
  FormDataType,
  TaskStepsData,
  FormStepsData,
  StepsDataTypeSend,
  FormGroupResponse,
} from '@/domain/form';
import { IhttpResponse } from '@/interfaces/http-interfaces';
import { CredentialsError, BackendError } from '../errors/api_errors';
import globalStore from '../store/globalStore';
import { recursiveGetForms } from '../utils';

const FORM_URL = `${import.meta.env.VITE_RADAR_URL}/form/`;

export const getForms = async (): Promise<ListElementType[]> => {
  const res: IhttpResponse = await getRequest(FORM_URL);
  if (res.status === 200) {
    const responese = res.body as FormGroupResponse;
    const forms = recursiveGetForms(responese);
    globalStore.dispatch('changeForms', forms);
    return forms;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const getFormsGroups = async (): Promise<FormGroupResponse> => {
  const res: IhttpResponse = await getRequest(FORM_URL);
  if (res.status === 200) {
    const responese = res.body as FormGroupResponse;
    const forms = recursiveGetForms(responese);
    globalStore.dispatch('changeForms', forms);
    return res.body as FormGroupResponse;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const getForm = async (
  id: number
): Promise<FormDataType<FieldsType>> => {
  const url = `${FORM_URL}${id}`;
  const res: IhttpResponse = await getRequest(url);
  if (res.status === 200) {
    return res.body as FormDataType<FieldsType>;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const postForm = async (
  name: string,
  description: string | null,
  groupId: number | null
): Promise<FormDataType<FieldsType>> => {
  const res: IhttpResponse = await postRequest(FORM_URL, {
    name: name,
    description: description,
    parent_id: groupId,
  });
  if (res.status === 200) {
    return res.body as FormDataType<FieldsType>;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const putForm = async (
  name: string,
  description: string | null,
  formId: number,
  groupId: number | null
): Promise<FormDataType<FieldsType>> => {
  const url = `${FORM_URL}${formId}`;
  const res: IhttpResponse = await putRequest(url, {
    name: name,
    description: description,
    parent_id: groupId,
  });
  if (res.status === 200) {
    return res.body as FormDataType<FieldsType>;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const deleteForm = async (id: number): Promise<boolean> => {
  const url = `${FORM_URL}${id}`;
  const res: IhttpResponse = await deleteRequest(url);
  if (res.status === 200) {
    return true;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const postFormFields = async (
  id: number,
  fields: FieldsType[]
): Promise<FormDataType<FieldsType>> => {
  const url = `${FORM_URL}${id}/fields`;
  const body = fields;
  const res: IhttpResponse = await postRequest(url, body);
  if (res.status === 200) {
    return res.body as FormDataType<FieldsType>;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const postGroup = async (name: string, groupId: number | null) => {
  const url = `${FORM_URL}group`;
  const res: IhttpResponse = await postRequest(url, {
    name: name,
    parent_id: groupId,
  });
  if (res.status === 200) {
    return res.body;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const deleteGroup = async (groupId: number) => {
  const url = `${FORM_URL}group/${groupId}`;
  const res: IhttpResponse = await deleteRequest(url);
  if (res.status === 200) {
    return res.body;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const putGroup = async (
  name: string,
  groupId: number,
  parentId: number | null
) => {
  const url = `${FORM_URL}group/${groupId}`;

  const res: IhttpResponse = await putRequest(url, {
    name: name,
    parent_id: parentId === 0 ? null : parentId,
  });
  if (res.status === 200) {
    return res.body;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const putFormFields = async (
  id: number,
  values: FieldsType[]
): Promise<FormDataType<FieldsType>> => {
  const url = `${FORM_URL}${id}/fields`;
  const body = values;
  const res: IhttpResponse = await putRequest(url, body);
  if (res.status === 200) {
    return res.body as FormDataType<FieldsType>;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const deleteFormFields = async (id: number[], form_id: number) => {
  const url = `${FORM_URL}${form_id}/fields`;
  const res: IhttpResponse = await deleteRequest(url, id);
  if (res.status === 200) {
    return res.body as FormDataType<FieldsType>;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const getSteps = async (id: number): Promise<FormStepsData[]> => {
  const url = `${FORM_URL}${id}/steps`;
  const res: IhttpResponse = await getRequest(url);
  if (res.status === 200) {
    return res.body as FormStepsData[];
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const putStep = async (
  id: number,
  step_id: number,
  step: StepsDataTypeSend
): Promise<TaskStepsData[]> => {
  const url = `${FORM_URL}${id}/steps/${step_id}`;
  const body = step;

  const res: IhttpResponse = await putRequest(url, body);
  if (res.status === 200) {
    return res.body as TaskStepsData[];
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const postStep = async (id: number, step: StepsDataTypeSend) => {
  const url = `${FORM_URL}${id}/steps`;
  const body = step;
  const res: IhttpResponse = await postRequest(url, body);
  if (res.status === 200) {
    return;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const deleteStep = async (formId: number, stepId: number) => {
  const url = `${FORM_URL}${formId}/steps/${stepId}`;
  const res: IhttpResponse = await deleteRequest(url);
  if (res.status === 200) {
    return;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const removeFormObserver = async (
  form_id: number | undefined,
  id: number,
  type: string
) => {
  const url = `${FORM_URL}${form_id}/spectators`;
  const body = { id, type };

  const res: IhttpResponse = await deleteRequest(url, body);
  if (res.status === 200) {
    return res.body as FormDataType<FieldsType>;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const addFormObserver = async (
  form_id: number | undefined,
  id: number,
  type: string
) => {
  const url = `${FORM_URL}${form_id}/spectators`;
  const body = { id, type };

  const res: IhttpResponse = await postRequest(url, body);
  if (res.status === 200) {
    return res.body as FormDataType<FieldsType>;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const addConstructorElement = async (
  form_id: number | undefined,
  constructorField: FieldsType[]
) => {
  const url = `${FORM_URL}${form_id}/fields`;

  const res: IhttpResponse = await postRequest(url, constructorField);
  if (res.status === 200) {
    return res.body as FormDataType<FieldsType>;
  }

  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }

  throw new BackendError(res.body);
};

export const updateConstructorElement = async (
  form_id: number | undefined,
  constructorField: FieldsType[]
) => {
  const url = `${FORM_URL}${form_id}/fields`;

  const res: IhttpResponse = await putRequest(url, constructorField);
  if (res.status === 200) {
    return res.body as FormDataType<FieldsType>;
  }

  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }

  throw new BackendError(res.body);
};
